'use client';

import { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { Image } from '@/components/ui/Image';
import type { Building } from '@repo/api';
import { cn } from '@/lib/cn';
import Carousel from '@/components/carousel/Carousel';
import { CarouselControls, CarouselCounter } from '@/components/carousel';
import type { FullScreenCarouselProps } from '@/components/carousel/FullScreenCarousel';
import { useDeferredReady } from '@/hooks/useDeferredReady';

const FullScreenCarousel = dynamic<FullScreenCarouselProps>(
  () => import('@/components/carousel/FullScreenCarousel').then(mod => mod.FullScreenCarousel),
  {
    loading: () => null,
    ssr: false,
  },
);

interface BuildingImageCarouselProps {
  building: Building;
  className?: string;
  /**
   * Distinguishes the mweb and dweb mounts, which render simultaneously and
   * are shown/hidden by breakpoint. `CarouselClient` resolves its track via
   * `document.getElementById(carouselId)` and filters document-level
   * `carousel-control` events on that id alone, so two mounts sharing an id
   * would both drive whichever one the DOM returned first.
   */
  instanceId?: string;
  /**
   * Eager-loads and preloads the first slide. Only the dweb mount is an LCP
   * candidate; the mweb mount sits far below the fold.
   */
  priority?: boolean;
  /**
   * `full` is the dweb sidebar hero: one flush image filling the column, with
   * the counter and share overlays on top of it.
   *
   * `cards` is the mweb treatment: individually rounded 4:3 cards with the
   * next one peeking, using the same gutter and peek values as the available
   * apartments carousel below so the two line up. The overlays are dropped
   * because they're positioned against the carousel's full-bleed box and would
   * land on the peeking card rather than on the active one.
   */
  layout?: 'full' | 'cards';
}

export function BuildingImageCarousel({
  building,
  className,
  instanceId,
  priority = true,
  layout = 'full',
}: BuildingImageCarouselProps) {
  const isCards = layout === 'cards';
  const images = useMemo(() => building.images || [], [building.images]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shouldLoadFullscreen, setShouldLoadFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  const buildingTitle = building.fullAddress || building.primaryAddress?.streetAddress || '';
  const isDeferredReady = useDeferredReady({
    skip: false,
    delayMs: 1500,
    scope: 'building-image-carousel',
  });

  const buildingKey = String(building.id ?? building.slug ?? buildingTitle);
  const carouselKey = instanceId ? `${buildingKey}-${instanceId}` : buildingKey;
  const carouselId = `building-images-${carouselKey}`;
  const fullscreenCarouselId = `building-images-fullscreen-${carouselKey}`;

  const openFullscreen = useCallback((index: number) => {
    setShouldLoadFullscreen(true);
    setFullscreenIndex(index);
    setIsFullscreen(true);
  }, []);

  const closeFullscreen = useCallback(() => setIsFullscreen(false), []);
  const showAllImages = isDeferredReady && images.length > 1;
  const visibleImages = showAllImages ? images : images.slice(0, 1);

  /**
   * A lone image has nothing to peek at, so the reserved peek window would just
   * leave dead space on its right. Dropping it lets the card fill the gutters
   * exactly, sitting 24px off both screen edges.
   *
   * Keyed off the full image count rather than the deferred slice so a
   * multi-image carousel reserves the window up front and doesn't resize its
   * first card when the remaining slides arrive.
   */
  const cardsPeek = images.length > 1 ? 24 : 0;

  const carouselItems = useMemo(
    () =>
      visibleImages.map((img, index) => ({
        id: `building-image-${carouselKey}-${index}`,
        content: (
          <button
            type="button"
            onClick={() => openFullscreen(index)}
            className={cn('w-full text-left cursor-pointer')}
            aria-label={`Open image ${index + 1} fullscreen`}
          >
            <Image
              aspectRatio="4:3"
              quality={60}
              src={img.url}
              alt={img.description || `${buildingTitle} - Image ${index + 1}`}
              // `200px` was too small for this hero carousel and caused blurry upscaling.
              // Request a closer-to-rendered-size asset across breakpoints.
              sizes="(max-width: 768px) 92vw, (max-width: 1280px) 66vw, 900px"
              priority={index === 0 ? priority : false}
              loading={index === 0 && priority ? 'eager' : 'lazy'}
              fetchPriority={index === 0 && priority ? 'high' : 'auto'}
              className={cn('w-full', isCards ? 'rounded-2xl' : 'md:rounded-2xl')}
            />
          </button>
        ),
      })),
    [buildingTitle, carouselKey, isCards, openFullscreen, priority, visibleImages],
  );

  const fullscreenItems = useMemo(
    () =>
      images.map((img, index) => ({
        id: `building-image-fullscreen-${carouselKey}-${index}`,
        src: img.url,
        alt: img.description || `${buildingTitle} - Image ${index + 1}`,
      })),
    [buildingTitle, carouselKey, images],
  );

  if (!images || images.length === 0) {
    const placeholder = (
      <div
        className={cn('relative bg-secondary rounded-2xl overflow-hidden', className)}
        style={{ aspectRatio: '4/3' }}
      >
        <div className="w-full h-full flex items-center justify-center">
          <p className="text-body-2 text-secondary">No images available</p>
        </div>
      </div>
    );

    /**
     * The cards mount sits in a full-bleed wrapper and leans on the carousel's
     * own `padStart`/`padEnd` for its 24px gutter. This placeholder replaces the
     * carousel, so without a gutter of its own it would run its rounded corners
     * off both screen edges. The inset has to come from a padded parent rather
     * than a margin here, because callers pass `w-full`.
     */
    return isCards ? <div className="px-6">{placeholder}</div> : placeholder;
  }

  return (
    <div className={cn('relative group', className)}>
      <Carousel
        // `relative z-0` prevents iOS/Safari paint + hit-testing issues with overlays on top of scroll containers.
        className={cn('relative z-0', !isCards && 'md:rounded-2xl')}
        carouselId={carouselId}
        items={carouselItems}
        perView={{ mobile: 1, tablet: 1, desktop: 1 }}
        peek={
          isCards
            ? { mobile: cardsPeek, tablet: 0, desktop: 0 }
            : { mobile: 0, tablet: 0, desktop: 0 }
        }
        padStart={isCards ? { mobile: 24, tablet: 0, desktop: 0 } : 0}
        padEnd={isCards ? { mobile: 24, tablet: 0, desktop: 0 } : 0}
        gap={16}
        shouldFreeScroll={false}
        ariaLabel="Building images"
      />

      {showAllImages && (
        <CarouselControls
          carouselId={carouselId}
          className={cn(
            'absolute inset-0 px-3 z-20 pointer-events-none',
            'hidden md:group-hover:flex',
          )}
          prevClassName={cn(
            `disabled:hidden`,
            'bg-bg-transparent',
            'border-0',
            'pointer-events-auto',
          )}
          nextClassName={cn(
            `disabled:hidden`,
            'bg-bg-transparent',
            'border-0',
            'pointer-events-auto',
            'ml-auto',
          )}
        />
      )}

      {showAllImages && !isCards && (
        <CarouselCounter
          carouselId={carouselId}
          totalSlides={images.length}
          className="md:hidden absolute bottom-8 right-4 z-10"
        />
      )}

      {shouldLoadFullscreen ? (
        <FullScreenCarousel
          open={isFullscreen}
          carouselId={fullscreenCarouselId}
          title={buildingTitle}
          initialIndex={fullscreenIndex}
          onClose={closeFullscreen}
          items={fullscreenItems}
        />
      ) : null}
    </div>
  );
}
